// web/src/pages/admin/ApprovalTypesPage.tsx
//
// Q-9 — Admin CRUD for approval types (`/api/approval-types`).
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  approvalTypesApi,
  type ApprovalTypeDto,
  type CreateApprovalTypeRequest,
  type UpdateApprovalTypeRequest,
} from "@/lib/quote-admin";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn } from "@/components/data-grid";
import { IntentButton } from "@/components/intent-button";
import { DetailModal } from "@/components/detail-modal";
import { RowActions } from "@/components/row-actions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus } from "lucide-react";
import { useConfirm } from "@/hooks/use-confirm";
import { toast } from "@/hooks/use-toast";

// ---------------------------------------------------------------------------
// Drawer form state
// ---------------------------------------------------------------------------

interface FormState {
  approvalTypeCode: string;
  description: string;
  requiresApprover: boolean;
  selfApprovalLimit: string; // string for input, convert to number | null
}

function emptyForm(): FormState {
  return {
    approvalTypeCode: "",
    description: "",
    requiresApprover: false,
    selfApprovalLimit: "",
  };
}

function dtoToForm(dto: ApprovalTypeDto): FormState {
  return {
    approvalTypeCode: dto.approvalTypeCode,
    description: dto.description,
    requiresApprover: dto.requiresApprover,
    selfApprovalLimit: dto.selfApprovalLimit != null ? String(dto.selfApprovalLimit) : "",
  };
}

// ---------------------------------------------------------------------------
// ApprovalTypesPage
// ---------------------------------------------------------------------------

export function ApprovalTypesPage() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<ApprovalTypeDto | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());

  const { data: types = [], isLoading } = useQuery({
    queryKey: ["approval-types"],
    queryFn: () => approvalTypesApi.list(),
  });

  const visible = types.filter((t) => !t.deleted);

  function openNew() {
    setEditing(null);
    setForm(emptyForm());
    setDrawerOpen(true);
  }

  function openEdit(t: ApprovalTypeDto) {
    setEditing(t);
    setForm(dtoToForm(t));
    setDrawerOpen(true);
  }

  function closeDrawer() {
    setDrawerOpen(false);
    setEditing(null);
  }

  const selfApprovalLimitNum = form.selfApprovalLimit
    ? Number(form.selfApprovalLimit)
    : null;

  const save = useMutation({
    mutationFn: async () => {
      if (editing) {
        const body: UpdateApprovalTypeRequest = {
          description: form.description,
          requiresApprover: form.requiresApprover,
          selfApprovalLimit: selfApprovalLimitNum,
        };
        await approvalTypesApi.update(editing.approvalTypeCode, body);
      } else {
        const body: CreateApprovalTypeRequest = {
          approvalTypeCode: form.approvalTypeCode,
          description: form.description,
          requiresApprover: form.requiresApprover,
          selfApprovalLimit: selfApprovalLimitNum,
        };
        await approvalTypesApi.create(body);
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["approval-types"] });
      toast.success(editing ? "Approval type updated." : "Approval type created.");
      closeDrawer();
    },
    onError: (err: Error) => toast.error(`Save failed: ${err.message}`),
  });

  const del = useMutation({
    mutationFn: (code: string) => approvalTypesApi.remove(code),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["approval-types"] });
      toast.success("Approval type deleted.");
    },
    onError: (err: Error) => {
      const inUse =
        err.message.toLowerCase().includes("409") ||
        err.message.toLowerCase().includes("in use");
      toast.error(
        inUse
          ? "Approval type is in use by one or more quotes."
          : `Delete failed: ${err.message}`,
      );
    },
  });

  async function handleDelete(t: ApprovalTypeDto) {
    const ok = await confirm({
      title: "Delete approval type?",
      body: t.approvalTypeCode,
      confirmLabel: "Delete",
      intent: "destructive",
    });
    if (ok) del.mutate(t.approvalTypeCode);
  }

  const cols: DataGridColumn<ApprovalTypeDto>[] = [
    { id: "code", header: "Code", accessor: (t) => t.approvalTypeCode },
    { id: "description", header: "Description", accessor: (t) => t.description },
    {
      id: "requiresApprover",
      header: "Requires approver",
      accessor: (t) => (t.requiresApprover ? "Yes" : "No"),
    },
    {
      id: "selfApprovalLimit",
      header: "Self-approval limit",
      accessor: (t) =>
        t.selfApprovalLimit != null
          ? new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(
              t.selfApprovalLimit,
            )
          : "—",
      align: "right",
    },
    {
      id: "quotesUsing",
      header: "Quotes using",
      accessor: (t) => t.quotesUsingCount,
      align: "right",
    },
    {
      id: "actions",
      header: "",
      accessor: (t) => {
        const inUse = t.quotesUsingCount > 0;
        return (
          <div className="flex justify-end pr-2">
            <RowActions
              label={t.approvalTypeCode}
              onEdit={() => openEdit(t)}
              // Always pass onDelete: RowActions only renders the item when this
              // is truthy, so gating it on !inUse removed the item entirely and
              // left the user with no explanation. Let deleteDisabled do the work.
              onDelete={() => void handleDelete(t)}
              deleteDisabled={inUse || del.isPending}
              deleteTooltip={inUse ? `In use by ${t.quotesUsingCount} quote(s)` : undefined}
            />
          </div>
        );
      },
      align: "right",
    },
  ];

  return (
    <>
      <ListPage backTo="/admin"
        title="Approval Types"
        primaryAction={
          <IntentButton onClick={openNew} aria-label="Add approval type">
            <Plus className="mr-2 h-4 w-4" /> Add Approval Type
          </IntentButton>
        }
      >
        <DataGrid
          rows={visible}
          columns={cols}
          rowKey={(t) => t.approvalTypeCode}
          totalRows={visible.length}
          pageIndex={0}
          pageSize={visible.length || 1}
          loading={isLoading}
          emptyHeadline="No approval types found."
          onPageChange={() => void 0}
        />
      </ListPage>

      <DetailModal
        open={drawerOpen}
        onOpenChange={(o) => {
          if (!o) closeDrawer();
        }}
        title={editing ? "Edit Approval Type" : "New Approval Type"}
      >
        <div className="space-y-4 p-4">
          {!editing && (
            <div>
              <Label htmlFor="at-code">Code *</Label>
              <Input
                id="at-code"
                value={form.approvalTypeCode}
                onChange={(e) =>
                  setForm((f) => ({ ...f, approvalTypeCode: e.target.value }))
                }
                placeholder="e.g. STANDARD"
              />
            </div>
          )}
          <div>
            <Label htmlFor="at-description">Description *</Label>
            <Input
              id="at-description"
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="Human-readable description"
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              id="at-requires"
              type="checkbox"
              checked={form.requiresApprover}
              onChange={(e) =>
                setForm((f) => ({ ...f, requiresApprover: e.target.checked }))
              }
            />
            <Label htmlFor="at-requires">Requires approver</Label>
          </div>
          <div>
            <Label htmlFor="at-limit">Self-approval limit ($)</Label>
            <Input
              id="at-limit"
              type="number"
              min={0}
              value={form.selfApprovalLimit}
              onChange={(e) =>
                setForm((f) => ({ ...f, selfApprovalLimit: e.target.value }))
              }
              placeholder="Leave blank for no limit"
            />
          </div>
          <div className="flex gap-2 pt-2">
            <Button
              onClick={() => save.mutate()}
              disabled={
                save.isPending ||
                !form.description ||
                (!editing && !form.approvalTypeCode)
              }
            >
              {save.isPending ? "Saving…" : "Save"}
            </Button>
            <Button variant="outline" onClick={closeDrawer}>
              Cancel
            </Button>
          </div>
        </div>
      </DetailModal>
    </>
  );
}
